'use client';

/**
 * MCP Documentation Page
 *
 * Public documentation for connecting Repetix to AI tools via MCP.
 * No authentication required.
 */

import React, { useState } from 'react';
import Link from 'next/link';
import { Copy, Check, MessageSquare, Zap, Terminal, ArrowRight } from 'lucide-react';

export default function DocsPage() {
  return (
    <div className="min-h-screen bg-black py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back to Home */}
        <Link
          href="/"
          className="inline-flex items-center text-white/60 hover:text-white transition-colors mb-8 group"
        >
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Repetix
        </Link>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Connect Repetix to AI tools
          </h1>
          <p className="text-xl text-white/60">
            Use your subscription data with Claude, Cursor, ChatGPT, and more.
          </p>
        </div>

        <div className="space-y-12">
          {/* Section 1: Get your API key */}
          <Section number={1} title="Get your API key">
            <p className="text-white/80 mb-4 leading-relaxed">
              Go to <strong className="text-white">Settings → Developer</strong> and generate a new API key.
              Copy it — you'll need it in the next step.
            </p>
            <Link
              href="/settings"
              className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors font-medium"
            >
              Open Settings
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Section>

          {/* Section 2: Claude Desktop setup */}
          <Section number={2} title="Connect to Claude Desktop">
            <p className="text-white/80 mb-6 leading-relaxed">
              Add Repetix to your Claude Desktop configuration file.
            </p>

            <div className="space-y-6">
              <div>
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-white/60" />
                  macOS
                </h4>
                <p className="text-white/60 text-sm mb-3">
                  Open: <code className="bg-white/10 px-2 py-0.5 rounded">~/.config/claude/claude_desktop_config.json</code>
                </p>
              </div>

              <CodeBlock
                code={`{
  "mcpServers": {
    "repetix": {
      "command": "npx",
      "args": ["-y", "repetix-mcp"],
      "env": {
        "REPETIX_API_KEY": "your-api-key-here"
      }
    }
  }
}`}
              />

              <div>
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-white/60" />
                  Windows
                </h4>
                <p className="text-white/60 text-sm mb-3">
                  Open: <code className="bg-white/10 px-2 py-0.5 rounded">%APPDATA%\claude\claude_desktop_config.json</code>
                </p>
                <p className="text-white/60 text-sm">
                  Use the same configuration as above.
                </p>
              </div>

              <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4">
                <p className="text-white/80 text-sm">
                  <strong className="text-white">Important:</strong> Replace <code className="bg-white/10 px-1.5 py-0.5 rounded text-violet-300">your-api-key-here</code> with
                  the key from step 1, then restart Claude Desktop.
                </p>
              </div>
            </div>
          </Section>

          {/* Section 3: What you can ask */}
          <Section number={3} title="What you can ask">
            <p className="text-white/80 mb-6 leading-relaxed">
              Once connected, try these prompts:
            </p>
            <div className="grid gap-3">
              <PromptExample icon={<MessageSquare className="w-4 h-4" />}>
                What subscriptions do I have?
              </PromptExample>
              <PromptExample icon={<MessageSquare className="w-4 h-4" />}>
                What's renewing this week?
              </PromptExample>
              <PromptExample icon={<MessageSquare className="w-4 h-4" />}>
                How much am I spending per month?
              </PromptExample>
              <PromptExample icon={<MessageSquare className="w-4 h-4" />}>
                What's my most expensive subscription?
              </PromptExample>
              <PromptExample icon={<MessageSquare className="w-4 h-4" />}>
                Show me subscriptions I might be wasting
              </PromptExample>
            </div>
          </Section>

          {/* Section 4: Available tools */}
          <Section number={4} title="For developers: Available MCP tools">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 pr-4 text-white font-medium">Tool</th>
                    <th className="text-left py-3 text-white font-medium">Description</th>
                  </tr>
                </thead>
                <tbody className="text-white/70">
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4"><code className="text-violet-300">list_subscriptions</code></td>
                    <td className="py-3">Get all tracked subscriptions</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4"><code className="text-violet-300">get_subscription</code></td>
                    <td className="py-3">Get details for a specific subscription</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4"><code className="text-violet-300">get_savings_summary</code></td>
                    <td className="py-3">Monthly/yearly totals and breakdown</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4"><code className="text-violet-300">get_cancellation_instructions</code></td>
                    <td className="py-3">How to cancel a specific service</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4"><code className="text-violet-300">update_subscription_status</code></td>
                    <td className="py-3">Mark a subscription as cancelled</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4"><code className="text-violet-300">export_data</code></td>
                    <td className="py-3">Export subscriptions as JSON or CSV</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4"><code className="text-violet-300">search_services</code></td>
                    <td className="py-3">Search our database of services</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <h4 className="text-white font-medium mb-3">Resources</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>
                  <code className="text-violet-300">subscriptions://all</code>
                  <span className="mx-2">—</span>
                  All subscription data
                </li>
                <li>
                  <code className="text-violet-300">spending://summary</code>
                  <span className="mx-2">—</span>
                  Spending overview
                </li>
              </ul>
            </div>
          </Section>

          {/* Section 5: Other integrations */}
          <Section number={5} title="Other integrations">
            <p className="text-white/80 mb-4 leading-relaxed">
              The Repetix API works with any tool that supports MCP or REST APIs.
            </p>
            <ul className="space-y-2 text-white/70 mb-6">
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-violet-400" />
                <strong className="text-white">Cursor:</strong> Same config as Claude Desktop
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-white/30" />
                <strong className="text-white/50">ChatGPT:</strong> <span className="text-white/50">Coming soon</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-white/30" />
                <strong className="text-white/50">Custom integrations:</strong> <span className="text-white/50">API docs coming soon</span>
              </li>
            </ul>
            <p className="text-white/60 text-sm">
              Need help? Contact{' '}
              <a href="mailto:info@repetix.com" className="text-violet-400 hover:text-violet-300 transition-colors">
                info@repetix.com
              </a>
            </p>
          </Section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <Link
            href="/"
            className="inline-flex items-center text-white/60 hover:text-white transition-colors group"
          >
            <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Repetix
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({
  number,
  title,
  children
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-zinc-900/80 backdrop-blur-lg border border-white/10 rounded-xl p-6 md:p-8">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-8 h-8 rounded-full bg-linear-to-br from-violet-400 to-pink-500 flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-sm">{number}</span>
        </div>
        <h2 className="text-2xl font-semibold text-white">{title}</h2>
      </div>
      <div className="pl-12">
        {children}
      </div>
    </section>
  );
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <pre className="bg-zinc-950 border border-white/10 rounded-lg p-4 overflow-x-auto text-sm font-mono text-white/80">
        {code}
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white/60 hover:text-white transition-all"
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}

function PromptExample({
  icon,
  children
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
      <span className="text-violet-400">{icon}</span>
      <span className="text-white/80">{children}</span>
    </div>
  );
}
